import React, {useState, useEffect, useImperativeHandle, forwardRef} from 'react';
import GoogleMapReact from 'google-map-react';
import * as PropTypes from "prop-types";
import { SmallLoader } from "Ncontracts.Common.UI";
import "./_style.scss";
import Marker from "./Marker";
import { imageCdnUrls } from "../../common/ImageCdnUrls";
import settings from "../../../configs/appSettings";
import mapOverlayService from "../../../services/MapOverlayService";
import MAP_OVERLAYS from "../../../constants/mapOverlays";
import { MapMarkerModel } from "./_model";
import MarkerClustererPlus from '@google/markerclustererplus';
import {format} from "date-fns";

const GoogleMap = forwardRef(function GoogleMap(props, ref) {
    const [isLoading, setIsLoading] = useState(false); // consider useAsync
    const [map, setMap] = useState();
    const [maps, setMaps] = useState();
    const [infoWindow, setInfoWindow] = useState();
    const [drawingManager, setDrawingManager] = useState();
    const [selectionDrawing, setSelectionDrawing] = useState();

    // const [markerDots, setMarkerDots] = useState();
    // const [clusterer, setClusterer] = useState();

    /**
     * NOTE FOR ANYONE USING GoogleMap WITHIN A CLASS COMPONENT:
     * "functionHoist" from props is necessary because the parent of this component needs to call the function "clearSelectionPolygon"
     * Here's an example of usage of this property in the parent component
     * functionHoist={functionRef => { this.myParentHandler = functionRef; }}
     *
     * apparently you can't pass React.createRef() to a functional component.
     */

    function clearSelectionPolygon() {
        drawingManager.setOptions({
            drawingControl: true
        });

        if (selectionDrawing !== undefined) {
            selectionDrawing.setMap(null);
        }
    }

    useImperativeHandle(
        ref,
        () => ({
            clearSelectionPolygon
        }),
    );
    const createDrawingManager = (maps) => new maps.drawing.DrawingManager({
        drawingMode: maps.drawing.OverlayType.POLYGON,
        drawingControl: true,
        drawingControlOptions: {
            position: maps.ControlPosition.TOP_CENTER,
            drawingModes: ['polygon']
        },
        polygonOptions: {
            strokeWeight: 0,
            fillOpacity: 0.45,
            fillColor: '#003DA5' // blue
        }
    });

    useEffect(() => {
        setIsLoading(true);
        clearMapOverlay();
        if (map && maps) {
            renderMapOverlay(map, maps);
        }
    }, [props.activeMapOverlayId]);

    useEffect(() => {
        renderMarkers();
    }, [props.markerList]);

    return <>
        {isLoading && <div className="loading-overlay">
                <SmallLoader/>
        </div>}
        <GoogleMapReact
            bootstrapURLKeys={{
                key: `${settings.googleMapsApiKey}`,
                language: 'en',
                libraries: 'drawing,places,geometry',
            }}
            defaultCenter={props.center}
            defaultZoom={props.zoom}
            yesIWantToUseGoogleMapApiInternals
            onChildMouseEnter={onChildMouseEnter}
            onChildMouseLeave={onChildMouseLeave}
            onGoogleApiLoaded={({map, maps}) => apiIsLoaded(map, maps)}
        >
            {renderMarkers()}
        </GoogleMapReact>
    </>;

    function apiIsLoaded(map, maps) {
        setMap(map);
        setMaps(maps);
        const newInfoWindow = new maps.InfoWindow;
        setInfoWindow(newInfoWindow);

        map.addListener('idle', () => {
            setIsLoading(false);
        });

        if (props.useDrawingManager) {
            activateDrawingManager(maps, map);
        }

        fitMarkersInMapFocus(maps, map);
        setDefaultZoomForMap(map, 6);

        setIsLoading(true);
        renderMapOverlay(map, maps);
        // renderClusters(map, maps, newInfoWindow);
    }

    function activateDrawingManager(maps, map) {
        const drawingManager = createDrawingManager(maps);
        setDrawingManager(drawingManager);
        drawingManager.setMap(map);
        addDrawingManagerListener(maps, drawingManager);
    }

    function fitMarkersInMapFocus(maps, map) {
        const bounds = getMapBounds(maps);
        map.fitBounds(bounds);
        bindResizeListener(map, maps, bounds);
    }

    function setDefaultZoomForMap(map, zoomMax) {
        const mapZoom = map.getZoom();
        map.setZoom(mapZoom > zoomMax ? zoomMax : mapZoom);
    }

    function renderMapOverlay(map, maps) {
        const mapToGet = props.activeMapOverlayId;

        if (mapToGet == MAP_OVERLAYS.NONE) {
            map.data.setStyle({visible: false});
            shiftMapSlightly(map, maps);
        } else {
            //event listeners must be removed when toggling between Map Overlays
            maps.event.clearListeners(map.data, 'mouseover');
            maps.event.clearListeners(map.data, 'mouseout');
            mapOverlayService.fetchMapOverlay(mapToGet).then(data => {
                map.data.addGeoJson(data);
                setMapStyles(map);
                shiftMapSlightly(map, maps);

                //this will need to be reworked as we introduce more map overlays
                if(mapToGet == MAP_OVERLAYS.FIRE_PERIMETER || mapToGet == MAP_OVERLAYS.TROPICAL_WEATHER){
                    attachMouseoverTooltipsToMapFeatures(map, maps, mapToGet);
                }
            });
        }
    }

    function renderMarkers() {
        const markerList = props.markerList
            .filter(marker => marker.latitude !== null || marker.longitude !== null)
            .map((markerInfo, index) => {
                return (
                    <Marker
                        key={index}
                        lat={offset(markerInfo.latitude)}
                        lng={offset(markerInfo.longitude)}
                        markerInfo={markerInfo}
                    />
                );
            });
        return markerList;
    }

    // This is the marker clustering logic from VendorConcentrationMap, but it doesn't support changing the icon on selection.
    function renderClusters(map, maps, infoWindow) {
        const dataIsMissing =
            maps === undefined ||
            (props.markerList === undefined
                && props.markerList < 1);

        if (dataIsMissing)
            return;

        const dots = props.markerList.map(buildMapMarker);

        attachMouseoverTooltipsToMarkers(dots, infoWindow);
        renderMarkerCluster(map, dots);
        // setMarkerDots(dots);

        function buildMapMarker(marker) {
            return new maps.Marker({
                position: {
                    lat: offset(marker.latitude),
                    lng: offset(marker.longitude)
                },
                icon: {
                    url: marker.icon,
                    origin: new maps.Point(0, 0),
                    anchor: new maps.Point(13, 13),
                    scaledSize: new maps.Size(25, 25)
                },
                locationName: marker.name,
                locationAddress: marker.address1,
                locationCity: marker.city,
                locationState: marker.state,
                locationPostalCode: marker.postalCode,
                // leave these custom ones off
                residualRiskName: marker.residualRiskName,
                inherentRiskName: marker.inherentRiskName
            });
        }
    }

    function offset(coordinate) {
        return +(parseFloat(coordinate) + (Math.random() - .5) / 1500);
    }

    function attachMouseoverTooltipsToMarkers(markers, infoWindow) {
        markers.forEach(function (marker) {
            marker.addListener('mouseover', function () {
                const infoWindowContent = buildInfoContent(marker);

                infoWindow.setContent(infoWindowContent);
                infoWindow.setPosition(marker.position);
                infoWindow.open(map);

                marker.addListener('mouseout', function () {
                    infoWindow.close();
                });

            });
        });
    }

    function buildInfoContent(marker) {
        const residualRiskName = marker.residualRiskName != null
            && marker.residualRiskName !== undefined
            ? marker.residualRiskName
            : "";

        const inherentRiskName = marker.inherentRiskName != null
            && marker.inherentRiskName !== undefined
            ? marker.inherentRiskName
            : "";


        const inherentRiskLine = marker.inherentRiskName !== null
            && marker.inherentRiskName !== undefined
            ? `<div class="iw-riskLevel-content">Inherent Risk Level: ${inherentRiskName}</div>`
            : "";

        const residualRiskLine = marker.residualRiskName !== null
            && marker.residualRiskName !== undefined
            ? `<div class="iw-riskLevel-content">Residual Risk Level: ${residualRiskName}</div>`
            : "";

        const infoWindowContent = (
            `<div id="iw-vendor-container">
                <div class="iw-vendor-title">${marker.locationName}</div>
                <div class="iw-vendor-content">${marker.locationAddress}</div>
                <div class="iw-vendor-content">${marker.locationCity} , ${marker.locationState} + ${marker.locationPostalCode}</div>
                ${residualRiskLine}
                ${inherentRiskLine}
            </div>`
        );

        return infoWindowContent;
    }

    function renderMarkerCluster(map, markers) {
        const clusterStyles = [
            {
                textColor: 'white',
                height: 30,
                width: 30,
                fontFamily: "Open Sans",
                className: 'custom-clustericon-1'
            },
            {
                textColor: 'white',
                height: 30,
                width: 30,
                fontFamily: "Open Sans",
                className: 'custom-clustericon-2'
            },
            {
                textColor: 'white',
                height: 40,
                width: 40,
                fontFamily: "Open Sans",
                className: 'custom-clustericon-3'
            }
        ];

        new MarkerClustererPlus(map, markers, {
            imagePath: 'https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m',
            gridSize: 10,
            minimumClusterSize: 2,
            averageCenter: true,
            styles: clusterStyles,
            clusterClass: 'custom-clustericon',
            maxZoom: 18
        });
        // setClusterer(clusters);
    }

    function getMapBounds(maps) {
        const bounds = new maps.LatLngBounds();

        props.markerList.forEach((marker) => {
            const {latitude, longitude} = marker;
            bounds.extend(new maps.LatLng(latitude, longitude));
        });
        return bounds;
    }

    function setSelectedMarkers(maps, selectionPolygon) {
        const selectedMarkers = props.markerList.filter(marker => {
            const markerLatLng = new maps.LatLng(marker.latitude, marker.longitude);
            return maps.geometry.poly.containsLocation(markerLatLng, selectionPolygon);
        });
        props.onSelect(selectedMarkers);
    }

    function addDrawingManagerListener(maps, drawingManager) {
        maps.event.addListener(drawingManager, 'overlaycomplete', (event) => {
            if (event.type !== maps.drawing.OverlayType.MARKER) {
                drawingManager.setDrawingMode(null);
                drawingManager.setOptions({
                    drawingControl: false
                });

                const selectedPolygon = event.overlay;
                setSelectionDrawing(selectedPolygon);
                selectedPolygon.type = event.type;
                setSelectedMarkers(maps, selectedPolygon);
            }
        });
    }

    function bindResizeListener(map, maps, bounds) {
        maps.event.addDomListenerOnce(map, 'idle', () => {
            maps.event.addDomListener(window, 'resize', () => {
                map.fitBounds(bounds);
            });
        });
    }

    function shiftMapSlightly(map, maps) {
        // shift the map a bit to trigger a new 'idle' event for google maps to emit. It's a shame there's no "data-loaded" event. that would be cool
        // I checked out the 'overlaycomplete' event used in addDrawingManagerListener(), but it looks like it only works with the drawing manager.
        const latitude = map.getCenter().lat();
        const longitude = map.getCenter().lng();
        map.setCenter(new maps.LatLng(latitude, longitude + .000000001));
    }

    function setMapStyles(map) {
        map.data.setStyle(function(feature) {
            const customProperties = feature.getProperty('customFeatureProperties');
            return {
              fillColor: customProperties.fillColor,
              fillOpacity: customProperties.fillOpacity,
              strokeColor: customProperties.strokeColor,
              strokeOpacity: customProperties.strokeOpacity,
              strokeWeight: customProperties.strokeWeight
            };
        });
    }

    function attachMouseoverTooltipsToMapFeatures(map, maps, mapOverlayId){
        const fireInfoWindow = new maps.InfoWindow();

        map.data.addListener('mouseover', (event) => {
            const infoWindowContent = buildInfoWindowContent(event.feature, mapOverlayId);
            fireInfoWindow.setContent(infoWindowContent);
            fireInfoWindow.setPosition(event.latLng);
            fireInfoWindow.open(map);

            map.data.addListener('mouseout', function () {
                fireInfoWindow.close();
            });
        });
    }

    function buildInfoWindowContent(feature, mapOverlayId) {
        if (mapOverlayId == MAP_OVERLAYS.FIRE_PERIMETER){
            return getFirePerimeterInfoWindowContent(feature);
        }

        else if (mapOverlayId == MAP_OVERLAYS.TROPICAL_WEATHER){
            return getTropicalWeatherInfoWindowContent(feature);
        }
    }

    function getFirePerimeterInfoWindowContent(feature) {
        const incidentName = feature.getProperty('poly_IncidentName');
        const gisAcres = feature.getProperty('poly_GISAcres');
        const createDate = feature.getProperty('poly_CreateDate');
        const dateCurrent = feature.getProperty('poly_DateCurrent');

        const incidentNameContent = incidentName != null && incidentName !== undefined
            ? `<div class="iw-metadata-row"><span class="iw-metadata-label">Incident Name: </span>
                <span>${incidentName}</span></div>`
            : "";

        const totalAcres = gisAcres != null && gisAcres !== undefined
            ? `<div class="iw-metadata-row"><span class="iw-metadata-label">Total Acres: </span>
                <span>${Math.round(gisAcres).toLocaleString()}</span></div>`
            : "";

        const createdDateContent = createDate != null && createDate !== undefined
            ? `<div class="iw-metadata-row"><span class="iw-metadata-label">Started: </span>
                <span>${format(new Date(createDate),'P')}</span></div>`
            : "";

        const dataLastUpdated = dateCurrent != null && dateCurrent !== undefined
            ? `<div class="iw-metadata-row"><span class="iw-metadata-label">Data Last Updated: </span>
                <span>${format(new Date(dateCurrent), 'P')}</span></div>`
            : "";

        const infoWindowContent =
            `<div>
                ${incidentNameContent}
                ${totalAcres}
                ${createdDateContent}
                ${dataLastUpdated}
            </div>`;

        return infoWindowContent;
    }

    function getTropicalWeatherInfoWindowContent(feature) {
        const prob5Day = feature.getProperty('prob5Day');
        const risk5Day = feature.getProperty('risk5Day');

        const probabilityContent = prob5Day != null && prob5Day !== undefined
        ? `<div class="iw-metadata-row"><span class="iw-metadata-label">5 Day Probability: </span>
            <span>${prob5Day}</span></div>`
        : "";

        const riskContent = risk5Day != null && risk5Day !== undefined
            ? `<div class="iw-metadata-row"><span class="iw-metadata-label">5 Day Risk Projection: </span>
                <span>${risk5Day}</span></div>`
            : "";

        const infoWindowContent = (
            `<div>
                ${probabilityContent}
                ${riskContent}
            </div>`
        );

        return infoWindowContent;
    }

    function clearMapOverlay() {
        if (map !== undefined) {
            map.data.forEach((feature) => {
                map.data.remove(feature);
            });
        }
    }

    function onChildMouseEnter(key, childProps) {
        // it seems like this method works in both normal and drawing(default) modes.
        // the geoJSON tooltips only work in normal mode.
        const latLng = new maps.LatLng(childProps.markerInfo.latitude, childProps.markerInfo.longitude);

        const contactTypeIcon = childProps.markerInfo.contactType === 0
                                ? `${imageCdnUrls.map_marker}comm-home.svg`
                                : `${imageCdnUrls.map_marker}comm-office-unselected.svg`;

        const infoWindowContent =
            `<div id="iw-container">
                <div class="iw-title">${childProps.markerInfo.displayName}</div>
                <div class="iw-content">
                    <div class="iw-addressContent">${childProps.markerInfo.addressInfo}</div>
                    <div class="iw-contactTypeContainer">
                        <img src=${contactTypeIcon} width="25" height="25"/>
                    </div>
                </div>
            </div>`;

        infoWindow.setContent(infoWindowContent);
        infoWindow.setPosition(latLng);
        infoWindow.open(map);
    }

    function onChildMouseLeave() {
        infoWindow.close();
    }
});

GoogleMap.defaultProps = {
    center: {lat: 40.73, lng: -73.93},
    zoom: 10,
    useDrawingManager: false
};

GoogleMap.propTypes = {
    clearRecipientTypeSelection: PropTypes.func,
    markerList: PropTypes.arrayOf(PropTypes.shape(MapMarkerModel)),
    onSelect: PropTypes.func,
    center: PropTypes.object,
    zoom: PropTypes.number,
    activeMapOverlayId: PropTypes.number,
    useDrawingManager: PropTypes.bool,
    functionHoist: PropTypes.func
};
export default GoogleMap;
